import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight, Download, Copy, Printer, Columns } from 'lucide-react'
import toast from 'react-hot-toast'
import { showDeleteConfirm, showSuccess, showError } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuthStore } from '@/store/auth.store'
import { empQualificationService } from '@/services/empQualificationService'
import { contractService } from '@/services/contractService'
import type { EmpQualificationListItem } from '@/api/types/empQualification.types'
import type { EmployeeOption } from '@/api/types'

function formatDate(d?: string): string {
  if (!d) return '—'
  try {
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
  } catch {
    return String(d).slice(0, 10)
  }
}

export const EmpQualificationList: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [list, setList] = useState<EmpQualificationListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  // Backend ki pagination parameters required ho sakti hain; yahan hum ek bada page fetch karke
  // frontend client-side pagination/search handle karte hain.
  const FETCH_PAGE_SIZE = 10000
  const [totalRecords, setTotalRecords] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    employeeName: true,
    degreeName: true,
    instituteName: true,
    startYear: true,
    endYear: true,
    createdDate: true,
    action: true,
  })

  useEffect(() => {
    if (!branchId) return
    contractService.getAllEmployees(branchId).then((data) => setEmployees(data ?? [])).catch(() => setEmployees([]))
  }, [branchId])

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const res = await empQualificationService.getAll(branchId, 1, FETCH_PAGE_SIZE)
        setList(res.data ?? [])
        setTotalRecords(res.totalRecords ?? 0)
      } catch (e: any) {
        const status = e?.response?.status
        const msg = (e?.response?.data?.message ?? e?.message ?? '').toLowerCase()
        const isNoData = status === 404 || /no data|not found|no records/.test(msg)
        setList([])
        setTotalRecords(0)
        if (!isNoData) toast.error(e?.response?.data?.message ?? 'Failed to load qualifications.')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [branchId])

  const getEmployeeName = (empId?: number): string => {
    if (!empId) return '—'
    const emp = employees.find((e) => e.empId === empId)
    if (!emp) return '—'
    return (emp.fullName ?? [emp.empFirstNameEn, emp.empSecondNameEn, emp.empLastNameEn].filter(Boolean).join(' ').trim()) || `Employee ${empId}`
  }

  const filteredList = useMemo(() => {
    if (!searchQuery.trim()) return list
    const q = searchQuery.toLowerCase()
    return list.filter(
      (r) =>
        getEmployeeName(r.empId).toLowerCase().includes(q) ||
        (r.empDegreeName ?? '').toLowerCase().includes(q) ||
        (r.empInstituteName ?? '').toLowerCase().includes(q) ||
        String(r.empStartYear ?? '').toLowerCase().includes(q) ||
        String(r.empEndYear ?? '').toLowerCase().includes(q)
    )
  }, [list, searchQuery, employees])

  const paginatedRows = useMemo(
    () => filteredList.slice((pageNumber - 1) * pageSize, pageNumber * pageSize),
    [filteredList, pageNumber, pageSize]
  )
  const filteredTotal = filteredList.length
  const totalPages = Math.max(1, Math.ceil(filteredTotal / pageSize))
  const visibleColumnCount =
    [columnVisibility.employeeName, columnVisibility.degreeName, columnVisibility.instituteName, columnVisibility.startYear, columnVisibility.endYear, columnVisibility.createdDate, columnVisibility.action].filter(Boolean).length || 1

  const handleAdd = () => navigate('/qualifications/new')
  const handleEdit = (id: number) => navigate(`/qualifications/${id}/edit`)

  const handleDelete = async (id: number) => {
    const confirmed = await showDeleteConfirm('Delete Qualification', 'Are you sure you want to delete this qualification?')
    if (!confirmed) return
    setDeletingId(id)
    try {
      await empQualificationService.delete(id)
      await showSuccess('Deleted', 'Qualification deleted successfully.')
      if (branchId) {
        const res = await empQualificationService.getAll(branchId, 1, FETCH_PAGE_SIZE)
        setList(res.data ?? [])
        setTotalRecords(res.totalRecords ?? 0)
      }
    } catch (e: any) {
      await showError('Delete failed', e?.response?.data?.message ?? e?.message ?? 'Failed to delete.')
    } finally {
      setDeletingId(null)
    }
  }

  const handleCsv = () => {
    const headers = ['Employee Name', 'Degree Name', 'Institute Name', 'Start Year', 'End Year', 'Created Date']
    const rows = filteredList.map((r) =>
      [
        getEmployeeName(r.empId),
        r.empDegreeName ?? '',
        r.empInstituteName ?? '',
        r.empStartYear ?? '',
        r.empEndYear ?? '',
        r.createdDate ? formatDate(r.createdDate) : '',
      ].join(',')
    )
    const csv = [headers.join(','), ...rows].join('\r\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'Qualifications.csv'
    a.click()
    URL.revokeObjectURL(url)
    toast.success('CSV downloaded.')
  }

  const handleCopy = () => {
    const text = filteredList
      .map((r) =>
        [getEmployeeName(r.empId), r.empDegreeName, r.empInstituteName, r.empStartYear, r.empEndYear, r.createdDate ? formatDate(r.createdDate) : ''].join('\t')
      )
      .join('\n')
    navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard.'))
  }

  const handlePrint = () => window.print()

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Qualifications" description="Manage employee qualifications." />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">Please select a branch first.</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Qualifications"
        description="Manage employee qualifications."
        action={{ label: 'Add', onClick: handleAdd, icon: Plus }}
      />
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Columns className="w-4 h-4 mr-2" />
                  Column Visibility
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[180px]">
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.employeeName}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, employeeName: !!v }))}
                >
                  Employee Name
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.degreeName}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, degreeName: !!v }))}
                >
                  Degree Name
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.instituteName}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, instituteName: !!v }))}
                >
                  Institute Name
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.startYear}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, startYear: !!v }))}
                >
                  Start Year
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.endYear}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, endYear: !!v }))}
                >
                  End Year
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={columnVisibility.createdDate}
                  onCheckedChange={(v) => setColumnVisibility((prev) => ({ ...prev, createdDate: !!v }))}
                >
                  Created Date
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" size="sm" onClick={handleCsv}>
              <Download className="w-4 h-4 mr-2" />
              CSV
            </Button>
            <Button variant="outline" size="sm" onClick={handleCopy}>
              <Copy className="w-4 h-4 mr-2" />
              Copy
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {columnVisibility.employeeName && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Employee Name
                    </TableHead>
                  )}
                  {columnVisibility.degreeName && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Degree Name
                    </TableHead>
                  )}
                  {columnVisibility.instituteName && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Institute Name
                    </TableHead>
                  )}
                  {columnVisibility.startYear && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Start Year
                    </TableHead>
                  )}
                  {columnVisibility.endYear && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      End Year
                    </TableHead>
                  )}
                  {columnVisibility.createdDate && (
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Created Date
                    </TableHead>
                  )}
                  {columnVisibility.action && (
                    <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      Action
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : paginatedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumnCount} className="text-center py-8 text-muted-foreground">
                      No data available in table
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map((row) => (
                    <TableRow key={row.id}>
                      {columnVisibility.employeeName && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {getEmployeeName(row.empId)}
                        </TableCell>
                      )}
                      {columnVisibility.degreeName && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.empDegreeName ?? '—'}</TableCell>
                      )}
                      {columnVisibility.instituteName && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.empInstituteName ?? '—'}</TableCell>
                      )}
                      {columnVisibility.startYear && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.empStartYear ?? '—'}</TableCell>
                      )}
                      {columnVisibility.endYear && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{row.empEndYear ?? '—'}</TableCell>
                      )}
                      {columnVisibility.createdDate && (
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatDate(row.createdDate)}</TableCell>
                      )}
                      {columnVisibility.action && (
                        <TableCell className="px-4 py-2 text-center align-middle">
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(row.id!)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(row.id!)}
                              disabled={deletingId === row.id}
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
            <div className="text-sm text-muted-foreground">
              Showing {filteredTotal === 0 ? 0 : (pageNumber - 1) * pageSize + 1} to{' '}
              {Math.min(pageNumber * pageSize, filteredTotal)} of {filteredTotal} entries
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={String(pageSize)}
                onValueChange={(v) => {
                  setPageSize(Number(v))
                  setPageNumber(1)
                }}
              >
                <SelectTrigger className="w-[90px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 25, 50, 100].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber <= 1 || loading}
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground px-2">
                Page {pageNumber} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={pageNumber >= totalPages || loading || totalRecords === 0}
                onClick={() => setPageNumber((p) => p + 1)}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
